import {lazy} from '@aboutyou/lazy-imports';
import {styled} from '@linaria/react';
import React, {FC} from 'react';
import {SearchBarResults} from '../Header/SearchBar/SearchBarResults';
import {SearchSuggestionsTrending} from '../SearchSuggestionsTrending/SearchSuggestionsTrending';
import {SearchSuggestionsTrendingSkeleton} from '../SearchSuggestionsTrending/SearchSuggestionsTrendingSkeleton';
import {recentlySearchedLabelsInterface} from './SearchSuggestionsHistory';
import {SearchSuggestionsSkeleton} from './SearchSuggestionsSkeleton';
import {SearchSuggestionsProps} from './SearchSuggestionsView';

const SearchSuggestions = lazy(
  () =>
    import('./SearchSuggestions.eager').then(mod => ({
      default: mod.SearchSuggestions,
    })),
  {fallback: <SearchSuggestionsSkeleton />},
);
const SearchSuggestionsHistory = lazy(
  () =>
    import('./SearchSuggestionsHistory').then(mod => ({
      default: mod.SearchSuggestionsHistory,
    })),
  {fallback: <SearchSuggestionsTrendingSkeleton />},
);

export const MIN_CHARS_TO_SEARCH = 2;

// Opening the empty panel is a waterfall rather than one load: the history
// chunk has to land before the trending chunk starts downloading.
// Asking for both up front turns those hops into one parallel batch.
export const preloadSearchPanelChunks = () => {
  SearchSuggestionsHistory.preload();
  SearchSuggestionsTrending.preload();
};

interface ConditionalSearchSuggestionsHistoryProps {
  search: string;
  handleRemoveInputFocus: VoidFunction;
  isInputFocused: boolean;
  renderSponsoredSlider?: boolean;
  shouldDisableSearchBarResults?: boolean;
}

export const ConditionalSearchSuggestionsHistory: FC<
  ConditionalSearchSuggestionsHistoryProps &
    Omit<SearchSuggestionsProps, 'className' | 'onClick'> &
    recentlySearchedLabelsInterface
> = ({
  isInputFocused,
  search,
  handleRemoveInputFocus,
  createSearchPath,
  onClickSuggestion,
  shouldDisableSearchBarResults,
  disableKeyNavigation,
  enableRightArrow,
  recentlySearchedLabels,
  onNavigateSuggestionList,
  enableSearchSuggestionsFocusOnTab,
  renderSponsoredSlider = true,
}) => {
  if (!isInputFocused) {
    return null;
  }

  const isTypedSearch = search.length >= MIN_CHARS_TO_SEARCH;

  const panel = isTypedSearch ? (
    <SearchSuggestions
      onClickSuggestion={onClickSuggestion}
      search={search}
      onClick={handleRemoveInputFocus}
      createSearchPath={createSearchPath}
      disableKeyNavigation={disableKeyNavigation}
      enableRightArrow={enableRightArrow}
      onNavigateSuggestionList={onNavigateSuggestionList}
      enableSearchSuggestionsFocusOnTab={enableSearchSuggestionsFocusOnTab}
      renderSponsoredSlider={renderSponsoredSlider}
    />
  ) : (
    <SearchSuggestionsHistory
      onClick={handleRemoveInputFocus}
      onClickSuggestion={onClickSuggestion}
      createSearchPath={createSearchPath}
      disableKeyNavigation={disableKeyNavigation}
      recentlySearchedLabels={recentlySearchedLabels}
      onNavigateSuggestionList={onNavigateSuggestionList}
    />
  );

  const panelKey = isTypedSearch ? 'suggestions' : 'history';

  if (shouldDisableSearchBarResults) {
    return (
      <FadeTransitionWrapper key={panelKey}>{panel}</FadeTransitionWrapper>
    );
  }

  return (
    <SearchBarResults animateOnEntry contentKey={panelKey}>
      {panel}
    </SearchBarResults>
  );
};

const FadeTransitionWrapper = styled.div`
  animation: fade 150ms ease-in;

  @keyframes fade {
    from {
      opacity: 0;
    }

    to {
      opacity: 1;
    }
  }
`;
